import { useEffect, useRef, useState } from 'react';
import { palette } from '../../theme';
import { usePortalPalette } from '../../theme/usePortalPalette';
import { Box, CircularProgress, Link, Stack, Typography } from '../../primitives';
import * as Icons from '../../icons';
import { AVATAR_SLOTS, avatarSlot, initialsOf } from '@munifocus/chat';
import { linkify } from './linkify';
import { MentionText } from '../mentions/MentionTextField';
import { iconSize } from '@munifocus/tokens';

/**
 * Avatar tints, one per slot from `avatarSlot`. Drawn from the brand family so
 * a thread of faces still reads as this product rather than a colour wheel.
 *
 * The `satisfies` is load-bearing: `avatarSlot` returns `0..AVATAR_SLOTS-1`, so
 * a palette shorter than that would index past the end and hand back an
 * `undefined` tint at runtime. This makes the mismatch a build error instead.
 */
function avatarTints(portal: ReturnType<typeof usePortalPalette>) {
  return [
    { bg: portal.brand.accentTint, fg: portal.brand.accentDark },
    { bg: portal.background.parch, fg: portal.text.secondary },
    { bg: portal.brand.accentMute, fg: portal.text.onDark },
    { bg: portal.brand.accent, fg: portal.text.onDark },
    { bg: portal.brand.accentDark, fg: portal.text.onDark },
  ] as const satisfies { length: typeof AVATAR_SLOTS };
}

export interface ThreadAttachment {
  fileName: string;
  url: string;
  sizeBytes: number;
}

/** One rendered message. All copy and formatting resolved by the caller. */
export interface ThreadMessage {
  id: string;
  /** Empty for a withdrawn message — check `deleted` before reading it. */
  body: string;
  authorName: string;
  /**
   * Author's user id, for the avatar tint. Null on rows with no author — a
   * system line, or a message whose user row is gone.
   */
  authorId: string | null;
  /** Pre-formatted timestamp; the app owns locale and date formatting. */
  timestamp: string;
  mine: boolean;
  deleted: boolean;
  edited: boolean;
  /** `system` renders as centred chrome, not as speech. */
  system: boolean;
  attachments: ThreadAttachment[];
  /**
   * Delivery state for one's own messages, shown as a tick beside the time.
   *
   * Resolved by the caller because "read" means *every other member has read
   * it*, and only the caller knows the membership. Omitted for incoming
   * messages, which carry no tick — you know you have read your own screen.
   */
  status?: 'sent' | 'read';
  /** Colleagues @mentioned in it, highlighted in the body. */
  mentions?: { id: string; label: string }[];
}

export interface MessageThreadProps {
  messages: ThreadMessage[];
  loading?: boolean;
  /** Display names of whoever is typing right now. */
  typingNames?: string[];
  labels: {
    empty: string;
    loading: string;
    deleted: string;
    edited: string;
    /** e.g. names => "Sam is typing…" — the caller handles plurals and locale. */
    typing: (names: string[]) => string;
    /** e.g. count => "Show 44 more words". */
    showMore: (count: number) => string;
    showLess: string;
    /** Accessible names for the delivery ticks. */
    sent: string;
    read: string;
  };
}

/**
 * The transcript.
 *
 * Presentational only, like the rest of this folder. Two behaviours are worth
 * knowing about:
 *
 * **The list is rendered oldest-last but the data arrives newest-first**, since
 * that is the order the API pages in. Reversing here rather than at the call
 * site means every portal gets the same reading order for free.
 *
 * **Auto-scrolling is conditional.** Jumping to the bottom on every new message
 * would yank the view away from someone reading back through a thread — so it
 * only scrolls when they were already near the bottom, or when the message is
 * their own (they just sent it, and expect to see it).
 */
export function MessageThread({ messages, loading, typingNames = [], labels }: MessageThreadProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const newest = messages[0];

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !newest) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    // 120px of slack: someone a line or two up is still "at the bottom" and
    // expects to follow the conversation.
    if (newest.mine || distanceFromBottom < 120) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [newest]);

  if (loading) {
    return (
      <Stack sx={{ alignItems: 'center', py: 6 }} role="status" aria-label={labels.loading}>
        <CircularProgress size={22} />
      </Stack>
    );
  }

  // Reversed to reading order. Copied first — `reverse()` mutates, and this
  // array belongs to the query cache.
  const inOrder = [...messages].reverse();

  return (
    <Box
      ref={scrollRef}
      sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}
      // A log, not a live region: announcing every message would talk over
      // someone reading, and screen readers handle `log` politely.
      role="log"
      aria-live="polite"
    >
      {inOrder.length === 0 ? (
        <Stack sx={{ alignItems: 'center', py: 6 }}>
          <Typography variant="body2" sx={{ color: palette.text.muted }}>
            {labels.empty}
          </Typography>
        </Stack>
      ) : (
        <Stack sx={{ gap: 1.5 }}>
          {inOrder.map((message) => (
            <MessageRow key={message.id} message={message} labels={labels} />
          ))}
        </Stack>
      )}

      {typingNames.length > 0 && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1.5, color: palette.text.muted, fontStyle: 'italic' }}
        >
          {labels.typing(typingNames)}
        </Typography>
      )}

      <div ref={bottomRef} />
    </Box>
  );
}

function MessageRow({
  message,
  labels,
}: {
  message: ThreadMessage;
  labels: MessageThreadProps['labels'];
}) {
  const portal = usePortalPalette();

  if (message.system) {
    return (
      <Typography
        variant="caption"
        sx={{ textAlign: 'center', color: palette.text.muted, py: 0.5 }}
      >
        {message.body}
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      // Own messages sit right, everyone else's left — the fastest way to read
      // who said what without checking every byline.
      sx={{
        justifyContent: message.mine ? 'flex-end' : 'flex-start',
        // Bottom-aligned so the face sits level with the last line of a long
        // message rather than floating beside its first.
        alignItems: 'flex-end',
        gap: 1,
      }}
    >
      {/* Incoming only. Your own column is unambiguous, and your own face
          repeated down the thread is noise. */}
      {!message.mine && <MessageAvatar name={message.authorName} id={message.authorId} />}

      <Box
        sx={{
          position: 'relative',
          maxWidth: '78%',
          px: 1.5,
          pt: 1,
          pb: 0.75,
          // Matches the mobile thread, which is itself ported from medone's:
          // an 18px bubble whose corner on the speaker's side cuts back to 4.
          borderRadius: '18px',
          borderBottomRightRadius: message.mine ? '4px' : undefined,
          borderBottomLeftRadius: message.mine ? undefined : '4px',
          backgroundColor: message.mine ? portal.brand.accent : palette.background.paper,
          color: message.mine ? palette.text.onDark : palette.text.primary,
          // Hairline on the light bubble only; the teal one reads as a shape
          // already, and a border on it would muddy the edge.
          border: message.mine ? 'none' : `1px solid ${palette.border.line}`,
          ...bubbleTail(message.mine, portal.brand.accent),
        }}
      >
        {!message.mine && (
          <Typography
            variant="caption"
            sx={{ display: 'block', fontWeight: 700, mb: 0.25, color: palette.text.secondary }}
          >
            {message.authorName}
          </Typography>
        )}

        {message.deleted ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.7 }}>
            {labels.deleted}
          </Typography>
        ) : (
          <ExpandableBody
            body={message.body}
            mine={message.mine}
            mentions={message.mentions}
            labels={labels}
          />
        )}

        {message.attachments.length > 0 && (
          <Stack sx={{ gap: 0.5, mt: 1 }}>
            {message.attachments.map((attachment) => (
              <Link
                key={attachment.url}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: message.mine ? palette.text.onDark : portal.brand.accent,
                  fontSize: 13,
                }}
              >
                <Icons.Paperclip size={iconSize.sm} aria-hidden />
                {attachment.fileName}
              </Link>
            ))}
          </Stack>
        )}

        <Stack
          direction="row"
          sx={{
            mt: 0.5,
            gap: 0.5,
            alignItems: 'center',
            justifyContent: message.mine ? 'flex-end' : 'flex-start',
            opacity: 0.7,
          }}
        >
          <Typography variant="caption">
            {message.timestamp}
            {message.edited && !message.deleted ? ` · ${labels.edited}` : ''}
          </Typography>
          {message.status && (
            <Box
              component="span"
              sx={{ display: 'inline-flex', lineHeight: 0 }}
              // The tick is the whole signal, so it carries the name rather than
              // being hidden as decoration.
              role="img"
              aria-label={message.status === 'read' ? labels.read : labels.sent}
            >
              {message.status === 'read' ? (
                <Icons.CheckCheck size={iconSize.sm} aria-hidden />
              ) : (
                <Icons.Check size={iconSize.sm} aria-hidden />
              )}
            </Box>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

/** Above this many words, a message is collapsed behind a "show more" control. */
const WORD_LIMIT = 60;

/**
 * A message body that collapses when it is long.
 *
 * Word-counted rather than character-counted or line-clamped: the control has to
 * say how much more there is ("Show 44 more words"), and only a word count is
 * both meaningful to a reader and stable across the two languages — a French
 * translation of the same text runs longer in characters and would clamp at a
 * different point for no reason the reader could see.
 *
 * Collapsing is per message and resets on nothing: someone who expanded a long
 * note keeps it open while they work, which is why this is local state and not
 * derived from anything upstream.
 */
function ExpandableBody({
  body,
  mine,
  mentions,
  labels,
}: {
  body: string;
  mine: boolean;
  mentions?: { id: string; label: string }[];
  labels: MessageThreadProps['labels'];
}) {
  const [expanded, setExpanded] = useState(false);

  const words = body.split(/\s+/).filter(Boolean);
  const isLong = words.length > WORD_LIMIT;
  const shown = !isLong || expanded ? body : `${words.slice(0, WORD_LIMIT).join(' ')}…`;

  return (
    <>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {/* Linked as React nodes, never as HTML: this text was written by
            another user, and `dangerouslySetInnerHTML` over it is the one thing
            a message renderer must not do. */}
        {mentions?.length ? <MentionText text={shown} mentions={mentions} /> : linkify(shown)}
      </Typography>
      {isLong && (
        <Box
          component="button"
          type="button"
          onClick={() => setExpanded((value) => !value)}
          sx={{
            mt: 0.5,
            p: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            textAlign: 'left',
            // Inherits the bubble's colour so it reads on teal and on white
            // without a second rule for each.
            color: 'inherit',
            textDecoration: 'underline',
            opacity: mine ? 0.9 : 1,
          }}
        >
          {expanded ? labels.showLess : labels.showMore(words.length - WORD_LIMIT)}
        </Box>
      )}
    </>
  );
}

/**
 * The speech-bubble tail, as CSS triangles on the bubble's own pseudo-elements.
 *
 * Borders rather than an SVG or a rotated square: a border triangle inherits
 * nothing, needs no extra element in the tree, and cannot be caught by the
 * thread's scroll container the way a transformed child can.
 *
 * The incoming bubble needs *two* triangles — a hairline-coloured one a pixel
 * behind a paper-coloured one — because a border triangle cannot itself carry a
 * border, and without the outer one the tail would float free of the outline
 * around the rest of the bubble. The outgoing bubble has no border, so one
 * triangle is the whole shape.
 */
/**
 * The face beside an incoming message: initials on a tint keyed to the author's
 * id, so the same colleague is the same colour in every thread and a rename
 * cannot recolour them.
 */
function MessageAvatar({ name, id }: { name: string; id: string | null }) {
  const tint = avatarTints(usePortalPalette())[id ? avatarSlot(id) : 0];
  return (
    <Box
      aria-hidden
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tint.bg,
        color: tint.fg,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.2,
        // Decorative: the byline above the bubble already names the author, so
        // a screen reader announcing the initials too would just repeat it.
        userSelect: 'none',
      }}
    >
      {initialsOf(name)}
    </Box>
  );
}

function bubbleTail(mine: boolean, accent: string) {
  // Same numbers as the mobile thread: a 10px border triangle raked 20 degrees
  // so it sweeps away from the bubble instead of hanging straight down.
  const SIZE = 10;
  const base = {
    content: '""',
    position: 'absolute',
    width: 0,
    height: 0,
    bottom: -8,
  } as const;

  if (mine) {
    return {
      '&::after': {
        ...base,
        right: -2,
        borderTop: `${SIZE}px solid ${accent}`,
        borderLeft: `${SIZE}px solid transparent`,
        transform: 'rotate(-20deg)',
      },
    } as const;
  }

  // The incoming bubble carries a hairline, and a border triangle cannot itself
  // have a border — so the outline is a second, slightly larger triangle sitting
  // a pixel behind the fill. Both rotate together or the two would separate.
  return {
    '&::before': {
      ...base,
      left: -3,
      borderTop: `${SIZE + 1}px solid ${palette.border.line}`,
      borderRight: `${SIZE + 1}px solid transparent`,
      transform: 'rotate(20deg)',
    },
    '&::after': {
      ...base,
      left: -2,
      borderTop: `${SIZE}px solid ${palette.background.paper}`,
      borderRight: `${SIZE}px solid transparent`,
      transform: 'rotate(20deg)',
    },
  } as const;
}
